import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";

// Enum options extracted from Ruby TimeSerieFile.statuses
export const statusOptions = [
  { value: "pending", label: "Pending" },
  { value: "extracted", label: "Extracted" },
  { value: "transformed", label: "Transformed" },
  { value: "loaded", label: "Loaded" },
  { value: "computed", label: "Computed" },
  { value: "error", label: "Error" },
  { value: "ignored", label: "Ignored" },
  { value: "pre_processed", label: "Pre Processed" },
];

export const filterTimeSerieFilesSchema = z.object({
  provider_config_code: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  status_equals: z.string().nullable().optional(),
  file_file_name_contains: z.string().nullable().optional(),
  is_manually_uploaded: z.boolean().optional(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  loaded_updated_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  loaded_updated_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterTimeSerieFilesSchema = z.infer<
  typeof filterTimeSerieFilesSchema
>;

export const fields: Field[] = [
  {
    name: "file_file_name_contains",
    label: "File Name",
    type: "input",
  },
  {
    name: "provider_config_code",
    label: "Provider Config",
    type: "asyncAutocomplete",
    base_url: "etl-quantilia/provider_configs",
    column_name: "code",
  },
  {
    name: "status_equals",
    label: "Status",
    type: "select",
    useTranslation: false,
    options: statusOptions,
  },
  {
    name: "created_at",
    type: "dateRange",
    label: "Created At",
    from: { name: "created_at_from", label: "From" },
    to: { name: "created_at_to", label: "To" },
  },
  {
    name: "is_manually_uploaded",
    label: "Manual Upload",
    type: "checkbox",
  },
];

export const client = "qtl - etl";

export function FilterTimeSerieFiles() {
  return (
    <FilterTable
      client={client}
      schema={filterTimeSerieFilesSchema}
      tableName="time_serie_files"
      customBuildPath={() => `${client}/time_serie_files`}
      fields={fields}
    />
  );
}

export const ConfigSchema = z.object({
  id: z.number(),
  code: z.string(),
  file_pattern: z.string().nullable().optional(),
  client: z.string().nullable().optional(),
  mime_type: z.string().nullable().optional(),
  template_file_id: z.number().optional(),
  template_file_name: z.string().nullable().optional(),
});

export const UploadSchema = z.object({
  config_name: z.union([
    z.string().min(1, "Configuration is required"),
    z.object({ text: z.string().min(1) }),
  ]),
  files: z.array(z.instanceof(File)).nonempty("You must add at least one file"),
});

export type UploadSchemaType = z.infer<typeof UploadSchema>;
export type ConfigSchemaType = z.infer<typeof ConfigSchema>;
